import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getChallenges } from '../api/confetti'
import type { Challenge } from '../api/types'
import { CheckIcon, Spinner } from '../components/icons'
import { plural } from '../lib/format'
import { useSession } from '../session/context'

/**
 * Ronde 22D — de challenges-tab.
 *
 * Eén regel per challenge, met wat er over die challenge te zeggen valt: deed
 * jij hem al, hoeveel gasten deden mee, of wanneer hij verschijnt. Vergrendelde
 * challenges blijven staan met hun moment erbij — ze verbergen zou betekenen
 * dat de lijst tijdens de avond onaangekondigd langer wordt.
 */
export function ChallengesPage() {
  const { slug, guest } = useSession()

  const challenges = useQuery({
    queryKey: ['challenges', slug, guest?.guestId ?? null],
    queryFn: ({ signal }) => getChallenges(slug, guest?.guestId, signal),
  })

  const list = challenges.data?.challenges ?? []
  const done = list.filter((challenge) => challenge.completedByGuest).length

  return (
    <div className="px-5 pt-4 pb-4 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:px-9 lg:pt-7 lg:pb-9">
      {/* De actieknop staat op desktop in AlbumLayout; hier alleen ruimte
          houden zodat een lange titel er niet onder schuift. */}
      <header className="lg:pr-64">
        <div>
          <h1 className="font-display text-[32px] leading-tight lg:text-[40px]">Challenges</h1>
          <p className="text-ink-soft mt-1.5 text-sm lg:text-[15px]">
            {list.length === 0
              ? 'Het bruidspaar heeft nog geen opdrachten klaargezet.'
              : `${done} van ${list.length}`}
          </p>
        </div>
      </header>

      {challenges.isPending && (
        <div className="flex justify-center py-16">
          <Spinner className="text-accent size-6" />
        </div>
      )}

      <ul className="mt-5 flex flex-col gap-2 lg:mt-7 lg:grid lg:grid-cols-2 lg:gap-3">
        {list.map((challenge) => (
          <li key={challenge.id}>
            <ChallengeRow challenge={challenge} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function ChallengeRow({ challenge }: { challenge: Challenge }) {
  const inside = (
    <>
      <span
        aria-hidden="true"
        className={
          'flex size-10 shrink-0 items-center justify-center rounded-full text-[15px] ' +
          (challenge.completedByGuest
            ? 'bg-accent text-accent-ink'
            : challenge.isLocked
              ? 'border-line-warm text-ink-ghost border'
              : 'bg-tint text-accent')
        }
      >
        {challenge.completedByGuest ? (
          <CheckIcon className="size-3" />
        ) : challenge.isLocked ? (
          '◷'
        ) : (
          (challenge.emoji ?? '◎')
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-medium lg:text-base">
          {challenge.title}
        </span>
        <span className="text-ink-faint mt-0.5 block truncate text-[13px]">
          {describe(challenge)}
        </span>
      </span>

      {!challenge.isLocked && (
        <span aria-hidden="true" className="text-ink-ghost shrink-0 text-lg">
          ›
        </span>
      )}
    </>
  )

  const shell =
    'rounded-notice flex w-full items-center gap-3.25 border p-3.25 px-3.5 text-left ' +
    (challenge.completedByGuest
      ? 'border-accent/45 bg-tint/40'
      : challenge.isLocked
        ? 'border-line-row bg-surface opacity-70'
        : 'border-line-card bg-surface transition-colors hover:border-line-warm hover:bg-notice')

  // Een vergrendelde challenge is geen bestemming: er valt nog niets te doen.
  if (challenge.isLocked) {
    return <div className={shell}>{inside}</div>
  }

  return (
    <Link to={challenge.id} className={shell}>
      {inside}
    </Link>
  )
}

/** De tweede regel: wat er over deze challenge te zeggen valt (22D). */
function describe(challenge: Challenge): string {
  if (challenge.isLocked && challenge.unlocksAtUtc) {
    return `Verschijnt om ${new Date(challenge.unlocksAtUtc).toLocaleTimeString('nl-BE', {
      hour: '2-digit',
      minute: '2-digit',
    })}`
  }

  if (challenge.isExpired) {
    return 'Voorbij'
  }

  if (challenge.completedByGuest) {
    const single = challenge.kind === 'Video' ? 'video' : 'foto'
    const many = challenge.kind === 'Video' ? "video's" : "foto's"

    return `Jij deed dit · ${plural(challenge.guestSubmissionCount, single, many)}`
  }

  const prefix = challenge.kind === 'Video' ? 'Video · ' : ''

  if (challenge.submissionCount === 0) {
    return `${prefix}nog niemand deed dit`
  }

  // "1 gast deed dit", "2 gasten deden dit" — het werkwoord verbuigt mee.
  const verb = challenge.submissionCount === 1 ? 'deed' : 'deden'

  return `${prefix}${plural(challenge.submissionCount, 'gast', 'gasten')} ${verb} dit`
}
